import _ from "lodash";
import { convertToDotNotation } from "@Features/viewer-modals/components/Modal/SearchSet/searchSetHelper";
import type { ModelPropertiesData } from "@WebTypes/modelProperties";
import { extractPropertyPartialName } from "@Features/viewer-modals/components/Modal/SearchSet1/searchSetHelper";
import { convertDate } from "@Services/luxonHelper";

/** A node of the legacy jQuery-QueryBuilder tree: a group (`condition` + `rules`) or a rule leaf. */
type LegacyQueryNode = {
  condition?: string;
  rules?: LegacyQueryNode[];
  field?: string;
  operator?: string;
  value?: unknown;
};

/** A rule leaf of the react-awesome-query-builder tree. */
type QueryRuleProperties = { field?: string | null; operator?: string; value?: unknown };

/** A child of a react-awesome-query-builder group: either a nested group or a rule. */
type QueryChild = { type?: string; properties: QueryRuleProperties };

/** A per-value test built by {@link getFieldOperatorValue}. */
type OperatorTest = (itemValue: unknown) => boolean;

export const escapeRegexCharacters = (value: unknown) => {
  if (value && typeof value === "string") {
    return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  }
  return value;
};

export const getNestedValue = (obj: unknown, field: string): unknown => {
  if (!field) return undefined;
  if (_.get(obj, field) && typeof _.get(obj, field) === "object") {
    // The guard above already established that the resolved value is an object.
    return field
      .split(".")
      .reduce(
        (acc: Record<string, unknown> | undefined, part: string) => acc?.[part] as Record<string, unknown> | undefined,
        obj as Record<string, unknown> | undefined,
      )!.displayText;
  }
  return field
    .split(".")
    .reduce(
      (acc: Record<string, unknown> | undefined, part: string) => acc?.[part] as Record<string, unknown> | undefined,
      obj as Record<string, unknown> | undefined,
    );
};

export const buildPredicate = (q: LegacyQueryNode | null | undefined): ((item: unknown) => boolean) => {
  if (!q || !Object.keys(q).length) {
    return () => true;
  }
  if (Object.hasOwn(q, "condition")) {
    // A `condition` node always carries its `rules`.
    const preds = q.rules!.map((rule) => buildPredicate(rule));
    if (q.condition === "and") {
      return (item: unknown) => preds.every((p) => p(item));
    }
    if (q.condition === "or") {
      return (item: unknown) => preds.some((p) => p(item));
    }
    return () => true;
  }
  // KNOWN BUG, preserved verbatim: `getFieldOperatorValue` returns undefined for an unknown operator
  // and a rule leaf may carry no field, both of which this dereferences. Erased assertions.
  const testFunction = getFieldOperatorValue({
    operator: q.operator,
    value: q.value,
  });
  return (item: unknown) => testFunction!(getNestedValue(item, q.field!));
};

const normalizeToList = (itemValue: unknown): unknown[] => {
  if (Array.isArray(itemValue)) return itemValue;
  if (itemValue && typeof itemValue === "object") return [(itemValue as { displayText?: unknown }).displayText];
  return itemValue != null ? [itemValue] : [];
};

export const getFieldOperatorValue = ({
  operator,
  value,
}: {
  operator?: string;
  value?: unknown;
}): OperatorTest | undefined => {
  if (Array.isArray(value) && value.length > 0) {
    value = value[0];
  }
  switch (operator) {
    case "contains":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`.*${escapeRegexCharacters(value)}.*`, "i").test(String(val)));
      };
    case "equal":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => val == value);
      };
    case "notequal":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => val !== value);
      };
    case "startswith":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`^${escapeRegexCharacters(value)}`, "i").test(val as string));
      };
    case "endswith":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`${escapeRegexCharacters(value)}$`, "i").test(val as string));
      };
    case "doesnotstartwith":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`^${escapeRegexCharacters(value)}`, "i").test(val as string));
      };
    case "doesnotendwith":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`${escapeRegexCharacters(value)}$`, "i").test(val as string));
      };
    case "doesnotcontain":
      return (itemValue: unknown) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`.*${escapeRegexCharacters(value)}.*`, "i").test(val as string));
      };
    case "isnotempty":
      return (itemValue: unknown) => itemValue !== undefined && itemValue !== "" && itemValue !== null;
    case "isempty":
      return (itemValue: unknown) => itemValue === undefined || itemValue === "" || itemValue === null;
    case "greaterthan":
      return (itemValue: unknown) => {
        // The comparison operators are applied to whatever the property holds, unchanged.
        return (itemValue as number) > (value as number);
      };
    case "greaterthanorequal":
      return (itemValue: unknown) => {
        return (itemValue as number) >= (value as number);
      };
    case "lessthan":
      return (itemValue: unknown) => {
        return (itemValue as number) < (value as number);
      };
    case "lessthanorequal":
      return (itemValue: unknown) => {
        return (itemValue as number) <= (value as number);
      };
  }
};

// Operators whose meaning is negative: a slash variant counts as "no match" only when ALL forms fail.
const NEGATIVE_OPERATORS: Set<string | undefined> = new Set([
  "notequal",
  "doesnotcontain",
  "doesnotstartwith",
  "doesnotendwith",
]);

/**
 * Slash-equivalent forms of a search value so that searching "x" also matches "/x" (and "/x" matches "x").
 * Mirrors buildWildcardPatterns in packages/server/fileOperations/fileSearchHelper.ts.
 */
export const buildSlashEquivalentValues = (value: unknown): unknown[] => {
  if (Array.isArray(value) && value.length > 0) value = value[0];
  if (typeof value !== "string") return [value];
  const stripped = value.replace(/^\/+/, "");
  const variants = new Set<string>([value]);
  if (stripped) {
    variants.add(stripped);
    variants.add(`/${stripped}`);
  }
  return [...variants];
};

/** Like getFieldOperatorValue, but a leading slash is optional so "x" and "/x" match each other. */
export const getSlashTolerantOperatorValue = ({ operator, value }: { operator?: string; value?: unknown }) => {
  const testFns = buildSlashEquivalentValues(value)
    .map((variant) => getFieldOperatorValue({ operator, value: variant }))
    .filter(Boolean);
  if (testFns.length <= 1) return testFns[0];
  const negate = NEGATIVE_OPERATORS.has(operator);
  // `filter(Boolean)` drops the undefined entries at runtime but does not narrow the type.
  return (itemValue: unknown) =>
    negate ? testFns.every((fn) => fn!(itemValue)) : testFns.some((fn) => fn!(itemValue));
};

const evalChild = (child: QueryChild, item: unknown, allColumns: Set<string>): boolean | null => {
  if (child.type === "group") {
    const groupResult = applyFilter([item], child);
    if (groupResult.allColumns.size === 0) return null;
    groupResult.allColumns.forEach((col) => allColumns.add(col));
    return groupResult.filteredData.length > 0;
  }
  let { field, operator, value } = child.properties;
  // An incomplete rule (no field selected yet) carries field === null. Such a
  // rule never matches, and its null field must not be collected as a grid
  // column or it crashes column-building in extractPropertyPartialName.
  if (field) allColumns.add(field);
  const resolvedValue = Array.isArray(value) ? value[0] : value;
  const needsValue = !(["isempty", "isnotempty"] as (string | undefined)[]).includes(operator);
  if (!field || !operator || (needsValue && (resolvedValue == null || resolvedValue === ""))) return false;
  const testFunction = getFieldOperatorValue({ operator, value });
  return testFunction ? testFunction(_.get(item, field)) : null;
};

export const applyFilter = <T>(
  data: T[],
  query: Record<string, unknown> | null | undefined,
): { filteredData: T[]; allColumns: Set<string> } => {
  // The query is a dynamic filter tree (react-awesome-query-builder shape).
  const q = query as { children1?: unknown[]; properties?: { conjunction?: string; not?: boolean } } | null | undefined;
  if (!q?.children1 || q.children1.length === 0) {
    return { filteredData: data, allColumns: new Set<string>() };
  }
  const children1 = q.children1;
  const conjunction = q.properties?.conjunction || "AND";
  const not = !!q.properties?.not;
  const allColumns = new Set<string>();
  const filteredData = data.filter((item) => {
    const childResults = children1
      .map((child) => evalChild(child as QueryChild, item, allColumns))
      .filter((result) => result !== null && result !== undefined);
    let result = childResults.every((r) => r === true);
    if (conjunction === "OR") result = childResults.includes(true);
    return not ? !result : result;
  });
  return { filteredData, allColumns };
};

export const getQueryResult = (
  modelPropertiesData: ModelPropertiesData,
  query: Record<string, unknown> | null | undefined,
) => {
  const flatModelProperties = Object.values(modelPropertiesData).flatMap((obj) => Object.values(obj));
  const { filteredData } = applyFilter(flatModelProperties, query);

  return filteredData.map((data) => ({
    id: `${data.fileVersionId}_${data.nodeId}`,
    ...convertToDotNotation(data),
  }));
};

export const getFieldValue = (
  field: string,
  raw: unknown,
  questionMapper?: Record<string, { question?: string; type?: string }>,
): { label: string; value: string } => {
  let label = field;
  let fieldType: string | undefined;

  if (field.startsWith("customProperties.")) {
    const id = extractPropertyPartialName(field);
    const mapped = questionMapper?.[id];
    label = mapped?.question ?? id;
    fieldType = mapped?.type;
  } else if (field.startsWith("properties.") || field.startsWith("typeProperties.")) {
    label = extractPropertyPartialName(field);
  }

  let value: string;
  if (raw === undefined || raw === null || raw === "") {
    value = "";
  } else if (fieldType === "date-time") {
    value = convertDate(Number(raw), "dateForTooltip");
  } else if (Array.isArray(raw)) {
    value = raw.join(", ");
  } else if (typeof raw === "object") {
    value = ((raw as { displayText?: string }).displayText ?? JSON.stringify(raw)) as string;
  } else {
    value = String(raw);
  }

  return { label, value };
};
